import { location } from 'cdui-js';
import { isH5User, userState } from '../../../stores/user';
import { pState, resetpState, retryInitProcess } from '../account/open-progress/store';

const OPEN_ACCOUNT_PATHS = ['/open-progress', '/account-result'];
const ADD_ACCOUNT_PATHS = ['/add-account', '/add-virtual-applyinfo'];

/**
 * 停留页：这些路径下不触发任何 applyStatus 自动跳转，
 * 跳转只能由页内按钮（如 app-download 的"立即开户"）主动触发。
 */
const SAFE_NO_REDIRECT_PATHS = ['/app-download'];

const isOnPath = (path: string, prefixes: string[]) =>
  prefixes.some((p) => path === p || path.startsWith(`${p}?`));

const isOnOpenAccountPath = (path: string) => isOnPath(path, OPEN_ACCOUNT_PATHS);
const isOnAddAccountPath = (path: string) => isOnPath(path, ADD_ACCOUNT_PATHS);
const isOnSafeNoRedirectPath = (path: string) => isOnPath(path, SAFE_NO_REDIRECT_PATHS);

function normalizePathname(path: string): string {
  const raw = (path || '').split('?')[0].replace(/\/+$/, '') || '/';
  return raw;
}

/**
 * App 嵌套 / 浏览器深链的业务 H5（入金、兑换、转入股票等）。
 * 不应被主站 applyStatus 门禁改写到开户/增开页；与 App WebView 内直接打开一致。
 */
const EMBEDDED_BUSINESS_H5_ROOTS = [
  '/deposits',
  '/withdrawals',
  '/exchange',
  '/exchange-currency',
  '/stock-transfer-in',
  '/fund-records',
  '/bank-list',
  '/trade-products',
  /** CRS 登记（路由别名 /crs；App 深链多为 /h5/crs，已由 pathname.startsWith('/h5/') 覆盖） */
  '/crs',
];

function pathnameMatchesEmbeddedBusinessH5(pathname: string): boolean {
  if (pathname.startsWith('/h5/')) return true;
  return EMBEDDED_BUSINESS_H5_ROOTS.some(
    (root) => pathname === root || pathname.startsWith(`${root}/`),
  );
}

function isOnEmbeddedBusinessH5Path(path: string): boolean {
  const fromRouter = normalizePathname(path);
  if (pathnameMatchesEmbeddedBusinessH5(fromRouter)) return true;
  if (typeof window !== 'undefined') {
    const fromBrowser = normalizePathname(window.location.pathname);
    if (fromBrowser !== fromRouter && pathnameMatchesEmbeddedBusinessH5(fromBrowser)) return true;
  }
  return false;
}

/**
 * 开户门禁只服务主站 SPA。活动/奖励等独立页即使误调本函数也不得改地址栏。
 */
function isOnActivityMarketingPath(path: string): boolean {
  const pathname = normalizePathname(
    path || (typeof window !== 'undefined' ? window.location.pathname : ''),
  );
  const lower = pathname.toLowerCase();
  return lower.endsWith('/activity') || lower.includes('/activity/');
}

/** 仅主站 SPA 入口（bootstrap / 登录成功）按 applyStatus 跳开户；活动页禁止调用 */
export const redirectH5ToOpenProgressIfNeeded = (
  applyStatus: string | null | undefined = userState.applyStatus,
) => {
  if (!isH5User()) return;
  if (!userState.isLogin) return;

  const path = location.path;

  // 停留页（如 app-download）：任何 applyStatus 都不自动跳走，
  // 跳转只能由页内按钮主动触发
  if (isOnSafeNoRedirectPath(path)) return;
  if (isOnActivityMarketingPath(path)) return;
  if (isOnEmbeddedBusinessH5Path(path)) return;

  const status = applyStatus ?? null;

  // 已开户完成 → 增开账户页
  if (status === 'FINISHED') {
    if (isOnAddAccountPath(path)) return;
    location.routeTo('/add-account');
    return;
  }

  // 已提交 / 审批中 → 结果页
  // TODO(debug): 本地调试开户页，临时关闭；调完务必恢复
  // if (status === 'SUBMITTED' || status === 'APPROVING') {
  //   if (isOnOpenAccountPath(path)) return;
  //   location.routeTo(`/account-result?scene=open-apply&type=${status}`);
  //   return;
  // }

  // 未开户 / 审批拒绝 → 开户页
  if (status === null || status === 'REFUSED' || status === 'BACK') {
    if (isOnOpenAccountPath(path)) {
      if (pState.loadError) void retryInitProcess();
      return;
    }
    resetpState();
    location.routeTo('/open-progress');
  }
};
